import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { API } from "../context/AuthContext";
import { useContent } from "../context/ContentContext";
import { toast } from "sonner";
import { Palette, Image as ImageIcon, Type, RotateCcw, Save, Upload, ExternalLink, History, Eye } from "lucide-react";
import AdminPages from "./AdminPages";

const inp = "w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm outline-none focus:border-[#FF1E27]";
const abs = (u) => (u && u.startsWith("/api/") ? `${API.replace(/\/api$/, "")}${u}` : u);
const GROUPS = [["brand", "Brand & colori", Palette], ["home", "Home page", Type], ["homevideo", "Home Video", Type], ["paddock", "Play Rally", Type], ["pricing", "Licenze", Type], ["season", "Campionato Fan", Type], ["sponsor", "Sponsor", ImageIcon]];
const PREVIEW_PAGES = [["/", "Home"], ["/paddock", "Play Rally"], ["/pricing", "Licenze"], ["/season", "Campionato Fan"]];
const GROUP_PAGE = { paddock: "/paddock", pricing: "/pricing", season: "/season" };

export default function AdminSiteEditor() {
  const [items, setItems] = useState([]);
  const [dirty, setDirty] = useState({});
  const [group, setGroup] = useState("brand");
  const { reload } = useContent();
  const [preview, setPreview] = useState(true);
  const [history, setHistory] = useState(null);
  const [previewPage, setPreviewPage] = useState("/");
  const frameRef = useRef();
  const pickGroup = (g) => { setGroup(g); if (GROUP_PAGE[g]) setPreviewPage(GROUP_PAGE[g]); else if (g === "home") setPreviewPage("/"); };
  useEffect(() => { frameRef.current?.contentWindow?.postMessage({ type: "rl-preview", overrides: dirty }, "*"); }, [dirty]);
  const loadHistory = async () => { const { data } = await axios.get(`${API}/admin/site/history`); setHistory(data); };
  const restore = async (h) => { await axios.put(`${API}/admin/site/content`, { items: [{ key: h.key, value: h.value }] }); toast.success("Versione ripristinata"); await load(); reload(); loadHistory(); };
  const load = () => axios.get(`${API}/admin/site/schema`).then(r => { setItems(r.data); setDirty({}); });
  useEffect(() => { load(); }, []);

  const save = async () => {
    const payload = Object.entries(dirty).map(([key, value]) => ({ key, value }));
    if (!payload.length) return toast.info("Nessuna modifica");
    await axios.put(`${API}/admin/site/content`, { items: payload });
    toast.success(`${payload.length} modifiche pubblicate sul sito`); await load(); reload();
  };
  const reset = async (key) => { await axios.delete(`${API}/admin/site/content/${key}`); toast.info("Ripristinato il valore originale"); await load(); reload(); };
  const upload = async (key, file) => {
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await axios.post(`${API}/admin/site/upload`, fd); setDirty({ ...dirty, [key]: data.url }); toast.success("Immagine caricata: premi PUBBLICA"); }
    catch (e) { toast.error(e.response?.data?.detail || "Upload fallito"); }
  };
  const val = (it) => (dirty[it.key] !== undefined ? dirty[it.key] : it.value);
  const shown = items.filter(it => it.key.startsWith(group + "."));

  return (
    <div className="rl-card p-6" data-testid="admin-site-editor">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
        <h2 className="font-display font-bold text-xl">Editor del sito</h2>
        <div className="flex gap-2">
          <button className="btn-outline-rl !py-2 !px-3 text-xs" onClick={() => setPreview(!preview)} data-testid="site-preview-toggle"><Eye className="w-4 h-4 inline mr-1" />{preview ? "NASCONDI ANTEPRIMA" : "ANTEPRIMA LIVE"}</button>
          <button className="btn-outline-rl !py-2 !px-3 text-xs" onClick={() => history ? setHistory(null) : loadHistory()} data-testid="site-history-btn"><History className="w-4 h-4 inline mr-1" />STORICO</button>
          <a href="/" target="_blank" rel="noreferrer" className="btn-outline-rl !py-2 !px-3 text-xs" data-testid="site-preview-link"><ExternalLink className="w-4 h-4 inline mr-1" />ANTEPRIMA</a>
          <button className="btn-crimson !py-2 !px-4 text-xs" onClick={save} data-testid="site-publish-btn"><Save className="w-4 h-4 inline mr-1" />PUBBLICA ({Object.keys(dirty).length})</button>
        </div>
      </div>
      <p className="text-xs text-slate-500 mb-4">Modifica testi, immagini e colori di tutta la piattaforma. Le modifiche sono visibili a tutti appena premi PUBBLICA.</p>
      <div className="flex flex-wrap gap-2 mb-5">
        {GROUPS.map(([g, label, Icon]) => (
          <button key={g} onClick={() => pickGroup(g)} data-testid={`site-group-${g}`}
            className={`px-3 py-1.5 rounded-full font-mono-rl text-[11px] uppercase tracking-widest flex items-center gap-1 ${group === g ? "bg-[#FF1E27] text-white" : "border border-white/10 text-slate-300"}`}><Icon className="w-3 h-3" /> {label}</button>
        ))}
      </div>
      {history && (
        <div className="border border-white/10 rounded-lg p-3 mb-5 max-h-64 overflow-y-auto" data-testid="site-history">
          <div className="text-xs font-mono-rl text-slate-400 tracking-widest mb-2">STORICO MODIFICHE · clicca RIPRISTINA per tornare a una versione</div>
          {history.length === 0 && <div className="text-xs text-slate-500">Nessuna modifica registrata.</div>}
          {history.map((h, i) => (
            <div key={i} className="flex items-center justify-between gap-3 text-xs border-b border-white/5 py-2" data-testid={`history-row-${i}`}>
              <div className="min-w-0"><span className="text-slate-400">{new Date(h.at).toLocaleString("it-IT")}</span> · <b>{h.label}</b><div className="truncate text-slate-500">{h.value?.startsWith("/api/") ? "(immagine)" : h.value}</div></div>
              <button className="btn-outline-rl !py-1 !px-2 text-[10px] shrink-0" onClick={() => restore(h)} data-testid={`history-restore-${i}`}>RIPRISTINA</button>
            </div>))}
        </div>
      )}
      <div className={`grid gap-4 ${preview ? "xl:grid-cols-2" : ""}`}>
      <div className="grid md:grid-cols-2 gap-4 content-start">
        {shown.map(it => <Field key={it.key} it={it} value={val(it)} changed={dirty[it.key] !== undefined} onChange={v => setDirty({ ...dirty, [it.key]: v })} onReset={() => reset(it.key)} onUpload={f => upload(it.key, f)} />)}
      </div>
      {preview && (
        <div className="rounded-xl overflow-hidden border border-white/10 bg-black min-h-[520px]" data-testid="site-live-preview">
          <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-1.5 border-b border-white/5">
            <span className="text-[10px] font-mono-rl text-slate-500">ANTEPRIMA LIVE · le modifiche non salvate sono visibili qui</span>
            <div className="flex gap-1" data-testid="site-preview-pages">
              {PREVIEW_PAGES.map(([p, label]) => (
                <button key={p} onClick={() => setPreviewPage(p)} data-testid={`site-preview-page-${p === "/" ? "home" : p.slice(1)}`}
                  className={`px-2 py-0.5 rounded text-[10px] font-mono-rl uppercase tracking-wider ${previewPage === p ? "bg-[#FF1E27] text-white" : "text-slate-400 hover:text-white"}`}>{label}</button>
              ))}
            </div>
          </div>
          <iframe ref={frameRef} title="preview" src={`${previewPage}?preview=1`} className="w-full h-[560px]" onLoad={() => frameRef.current?.contentWindow?.postMessage({ type: "rl-preview", overrides: dirty }, "*")} />
        </div>
      )}
      </div>
      <AdminPages />
    </div>
  );
}

function Field({ it, value, changed, onChange, onReset, onUpload }) {
  const fileRef = useRef();
  const id = it.key.replace(/\./g, "-");
  return (
    <div className={`border rounded-lg p-3 ${changed ? "border-[#FF9F0A]/60" : "border-white/5"}`} data-testid={`site-field-${id}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-mono-rl text-slate-400 tracking-widest">{it.label}</div>
        <button onClick={onReset} className="text-slate-500 hover:text-white" title="Ripristina originale" data-testid={`site-reset-${id}`}><RotateCcw className="w-3 h-3" /></button>
      </div>
      {it.type === "text" && <input className={inp} value={value} onChange={e => onChange(e.target.value)} data-testid={`site-input-${id}`} />}
      {it.type === "textarea" && <textarea className={`${inp} min-h-[80px]`} value={value} onChange={e => onChange(e.target.value)} data-testid={`site-input-${id}`} />}
      {it.type === "color" && (
        <div className="flex items-center gap-2">
          <input type="color" value={value} onChange={e => onChange(e.target.value)} className="w-10 h-10 rounded bg-transparent border-0" data-testid={`site-color-${id}`} />
          <input className={inp} value={value} onChange={e => onChange(e.target.value)} data-testid={`site-input-${id}`} />
        </div>
      )}
      {it.type === "video" && (
        <div className="flex items-center gap-3">
          <video src={abs(value)} muted playsInline preload="metadata" className="w-24 h-16 rounded bg-black/40 border border-white/10 object-cover" />
          <div className="flex-1 space-y-2">
            <input className={inp} value={value} onChange={e => onChange(e.target.value)} placeholder="URL video o carica (mp4/webm, max 12 MB)" data-testid={`site-input-${id}`} />
            <input ref={fileRef} type="file" accept="video/mp4,video/webm" className="hidden" onChange={e => e.target.files[0] && onUpload(e.target.files[0])} data-testid={`site-file-${id}`} />
            <button className="btn-outline-rl !py-1.5 !px-3 text-xs" onClick={() => fileRef.current.click()} data-testid={`site-upload-${id}`}><Upload className="w-3 h-3 inline mr-1" />CARICA VIDEO</button>
          </div>
        </div>
      )}
      {it.type === "image" && (
        <div className="flex items-center gap-3">
          <div className="w-24 h-16 rounded bg-black/40 border border-white/10 flex items-center justify-center overflow-hidden">
            {value ? <img src={abs(value)} alt="" className="max-w-full max-h-full object-contain" /> : <ImageIcon className="w-5 h-5 text-slate-600" />}
          </div>
          <div className="flex-1 space-y-2">
            <input className={inp} value={value} onChange={e => onChange(e.target.value)} placeholder="URL immagine o carica" data-testid={`site-input-${id}`} />
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={e => e.target.files[0] && onUpload(e.target.files[0])} data-testid={`site-file-${id}`} />
            <button className="btn-outline-rl !py-1.5 !px-3 text-xs" onClick={() => fileRef.current.click()} data-testid={`site-upload-${id}`}><Upload className="w-3 h-3 inline mr-1" />CARICA IMMAGINE</button>
          </div>
        </div>
      )}
    </div>
  );
}
